import * as React from "react";

import { Calendar } from "@/shared/foundation/icons";
import {
  DateTimePicker,
  Popover,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { formatWindow } from "./formatRange";
import { useTimelineContext } from "./TimelineContext";
import styles from "./TimelineControls.module.css";
import { centerWindow } from "./window";

/**
 * The window as a date range — "Apr 16, 2026 – Jul 22, 2026 CDT" — that
 * follows every pan and zoom, so the span is always readable without
 * decoding the axis. It is a button: pressing it opens a popover with the
 * jump form, which closes once a jump lands or on Cancel.
 */
export function WindowRange() {
  const { timeWindow, format } = useTimelineContext();
  const [open, setOpen] = React.useState(false);
  const { text, zone } = formatWindow(timeWindow, format);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <RobotoButton
            variant="ghost"
            size="sm"
            icon={<Calendar />}
            className={styles.range}
          >
            <span className="sr-only">Time window, </span>
            <span className={styles.rangeText}>{text}</span>
            {zone !== "" && <span className={styles.rangeZone}>{zone}</span>}
            <span className="sr-only">, change</span>
          </RobotoButton>
        }
      />
      <Popover.Content className={styles.jumpPopover}>
        <Popover.Title>Jump to a date or time</Popover.Title>
        <DatePickerForm onJumped={() => setOpen(false)} />
      </Popover.Content>
    </Popover>
  );
}

interface JumpFormProps {
  /** Called after a jump moved the window; the popover that hosts the form closes on it. */
  onJumped: () => void;
}

/**
 * Moves the window to center on an instant the user picks, keeping its span.
 * The picker opens on the window's current center. An instant before the
 * first session or after now shows an inline error and leaves the window
 * where it is.
 */
function DatePickerForm({ onJumped }: JumpFormProps) {
  const { timeWindow, extent, setTimeWindow, format } = useTimelineContext();
  const [seed] = React.useState(() =>
    new Date((timeWindow.start + timeWindow.end) / 2).toISOString(),
  );
  const [picked, setPicked] = React.useState<string | null>(seed);
  const [error, setError] = React.useState<string | null>(null);

  const jump = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (picked === null) {
      return;
    }
    const instant = Date.parse(picked);

    const edge = (ms: number) =>
      format.format(new Date(ms), { time: "minutes" });

    if (instant < extent.start) {
      setError(`Before the first session (${edge(extent.start)})`);
      return;
    }

    if (instant > extent.end) {
      setError(`After now (${edge(extent.end)})`);
      return;
    }

    setError(null);
    setTimeWindow((window) => centerWindow(window, instant));

    onJumped();
  };

  return (
    <form className={styles.jumpForm} onSubmit={jump}>
      <DateTimePicker
        value={seed}
        zone={format.zone}
        invalid={error !== null}
        onChange={(next) => {
          setPicked(next);
          setError(null);
        }}
      />
      {error !== null && (
        <p className={styles.jumpError} role="alert">
          {error}
        </p>
      )}
      <div className={styles.jumpActions}>
        <Popover.Close
          render={<RobotoButton variant="secondary">Cancel</RobotoButton>}
        />
        <RobotoButton type="submit" disabled={picked === null}>
          Jump
        </RobotoButton>
      </div>
    </form>
  );
}
